// ============================================
// NoteNavigator - Motor de Navegación Mnemotécnica
// "El dibujito del profe" - Rutas de saltos en el diapasón
// ============================================

import {
  noteToSemitones,
  semitonesToNote,
  getNoteAtPosition,
  type NoteName,
  type NeckPosition,
} from "./guitarEngine";
import {
  getNoteAtPositionInstrument,
  type InstrumentType,
} from "./instrumentEngine";

export interface NavCoord {
  string: number; // 6=gruesa, 1=delgada
  fret: number;   // 0-24
  midi: number;
  note: NoteName;
  octave: number;
}

export interface NavStep {
  from: NavCoord;
  to: NavCoord;
  label: string;
  arrow: string;
  stepNumber: number;
}

export interface NoteRoute {
  anchor: NavCoord;
  steps: NavStep[];
  allCoords: NavCoord[]; // todos los puntos de la ruta incluyendo ancla
}

// ─── Grafo de saltos del profesor ───
// Cada cuerda tiene un destino fijo y un delta de traste.
// La ruta forma un ciclo que cubre las 6 cuerdas:
// 2 → 5 → 3 → 1 → 6 → 4 → 2 ...
export const JUMP_GRAPH: Record<
  number,
  { to: number; fretDelta: number; label: string; arrow: string }
> = {
  2: { to: 5, fretDelta: +2, label: "salto 1 espacio", arrow: "↙" },
  5: { to: 3, fretDelta: +2, label: "salto 1 espacio", arrow: "↗" },
  3: { to: 1, fretDelta: +3, label: "salto 2", arrow: "↗" },
  1: { to: 6, fretDelta: 0, label: "paralelismo", arrow: "⇅" },
  6: { to: 4, fretDelta: +2, label: "salto 2", arrow: "↗" },
  4: { to: 2, fretDelta: +2, label: "salto 1 espacio", arrow: "↗" },
};

// Nombres de cuerdas para UI
export const STRING_NAMES: Record<number, string> = {
  6: "Mi (gruesa)",
  5: "La",
  4: "Re",
  3: "Sol",
  2: "Si",
  1: "Mi (aguda)",
};

const NOTA_NAMES_ES: Record<string, string> = {
  C: "Do", "C#": "Do#", Db: "Reb",
  D: "Re", "D#": "Re#", Eb: "Mib",
  E: "Mi", F: "Fa", "F#": "Fa#", Gb: "Solb",
  G: "Sol", "G#": "Sol#", Ab: "Lab",
  A: "La", "A#": "La#", Bb: "Sib",
  B: "Si",
};

// ─── Helpers ───

function getStringBaseMidi(stringNum: number, instrument: InstrumentType = "guitar"): number {
  return getNoteAtPositionInstrument(stringNum, 0, instrument).midi;
}

export function noteNameToPitchClass(name: string): number {
  const clean = name.trim();
  // Soportar tanto nombres ingleses como españoles
  const classes: Record<string, number> = {
    Do: 0, "Do#": 1, Reb: 1,
    Re: 2, "Re#": 3, Mib: 3,
    Mi: 4,
    Fa: 5, "Fa#": 6, Solb: 6,
    Sol: 7, "Sol#": 8, Lab: 8,
    La: 9, "La#": 10, Sib: 10,
    Si: 11,
    C: 0, "C#": 1, Db: 1,
    D: 2, "D#": 3, Eb: 3,
    E: 4, F: 5, "F#": 6, Gb: 6,
    G: 7, "G#": 8, Ab: 8,
    A: 9, "A#": 10, Bb: 10,
    B: 11,
  };
  return classes[clean] ?? 0;
}

export function pitchClassToSpanishName(pc: number, preferFlat = false): string {
  const sharpNames = ["Do", "Do#", "Re", "Re#", "Mi", "Fa", "Fa#", "Sol", "Sol#", "La", "La#", "Si"];
  const flatNames = ["Do", "Reb", "Re", "Mib", "Mi", "Fa", "Solb", "Sol", "Lab", "La", "Sib", "Si"];
  const idx = ((pc % 12) + 12) % 12;
  return preferFlat ? flatNames[idx] : sharpNames[idx];
}

export function getOctaveLabel(midi: number, noteName?: string): string {
  const oct = Math.floor(midi / 12) - 1;
  const name = noteName ? (NOTA_NAMES_ES[noteName] || noteName) : "";
  return `${name}${oct}`;
}

/**
 * Construye la ruta mnemotécnica completa desde un punto de anclaje.
 * Sigue el grafo de saltos del profesor hasta cubrir todas las cuerdas
 * o hasta que un salto sea imposible (fuera de rango).
 */
export function buildRoute(
  startString: number,
  startFret: number,
  maxFret: number = 15,
  instrument: InstrumentType = "guitar"
): NoteRoute | null {
  const baseMidi = getStringBaseMidi(startString, instrument);
  const targetMidi = baseMidi + startFret;
  const pitchClass = targetMidi % 12;

  const anchor: NavCoord = {
    string: startString,
    fret: startFret,
    midi: targetMidi,
    note: semitonesToNote(pitchClass, false),
    octave: Math.floor(targetMidi / 12) - 1,
  };

  const steps: NavStep[] = [];
  const allCoords: NavCoord[] = [anchor];
  let current: NavCoord = anchor;
  const visited = new Set<string>();
  visited.add(`${current.string}-${current.fret}`);

  // Seguimos saltando mientras haya un siguiente nodo en el grafo
  // y no repitamos posiciones
  while (true) {
    const jump = JUMP_GRAPH[current.string];
    if (!jump) break;

    const nextFret = current.fret + jump.fretDelta;
    if (nextFret < 0 || nextFret > maxFret) break;

    const nextBaseMidi = getStringBaseMidi(jump.to);
    const nextMidi = nextBaseMidi + nextFret;

    // Verificación: debe ser la misma clase de nota (misma nota en otra octava)
    if (nextMidi % 12 !== pitchClass) break;

    const nextCoord: NavCoord = {
      string: jump.to,
      fret: nextFret,
      midi: nextMidi,
      note: semitonesToNote(pitchClass, false),
      octave: Math.floor(nextMidi / 12) - 1,
    };

    const key = `${nextCoord.string}-${nextCoord.fret}`;
    if (visited.has(key)) break;
    visited.add(key);

    steps.push({
      from: { ...current },
      to: { ...nextCoord },
      label: jump.label,
      arrow: jump.arrow,
      stepNumber: steps.length + 1,
    });

    allCoords.push(nextCoord);
    current = nextCoord;

    // Seguridad: no más de 10 pasos (ciclo completo son 6)
    if (steps.length >= 10) break;
  }

  return { anchor, steps, allCoords };
}

/**
 * Encuentra TODAS las posiciones de una nota en el diapasón.
 */
export function getAllPositions(
  noteName: string,
  maxFret: number = 15,
  instrument: InstrumentType = "guitar"
): NavCoord[] {
  const pitchClass = noteNameToPitchClass(noteName);
  const positions: NavCoord[] = [];

  const stringCount = instrument === "ukulele" ? 4 : 6;
  for (let s = 1; s <= stringCount; s++) {
    const baseMidi = getStringBaseMidi(s, instrument);
    for (let f = 0; f <= maxFret; f++) {
      if ((baseMidi + f) % 12 === pitchClass) {
        const midi = baseMidi + f;
        positions.push({
          string: s,
          fret: f,
          midi,
          note: semitonesToNote(pitchClass, false),
          octave: Math.floor(midi / 12) - 1,
        });
      }
    }
  }

  return positions;
}

/**
 * Encuentra el mejor punto de anclaje para construir la ruta completa.
 * Prefiere cuerda 2 (Si) con traste cómodo (≤12).
 */
export function findBestAnchor(
  noteName: string,
  maxFret: number = 15,
  instrument: InstrumentType = "guitar"
): NavCoord | null {
  const pitchClass = noteNameToPitchClass(noteName);
  const stringCount = instrument === "ukulele" ? 4 : 6;

  // Preferir cuerda 2 (o la más aguda disponible en ukelele)
  const preferredString = Math.min(2, stringCount);
  const basePref = getStringBaseMidi(preferredString, instrument);
  for (let f = 0; f <= Math.min(12, maxFret); f++) {
    if ((basePref + f) % 12 === pitchClass) {
      const midi = basePref + f;
      return {
        string: preferredString,
        fret: f,
        midi,
        note: semitonesToNote(pitchClass, false),
        octave: Math.floor(midi / 12) - 1,
      };
    }
  }

  // Fallback: buscar en cualquier cuerda, traste más bajo posible
  for (let s = 1; s <= stringCount; s++) {
    const base = getStringBaseMidi(s, instrument);
    for (let f = 0; f <= Math.min(12, maxFret); f++) {
      if ((base + f) % 12 === pitchClass) {
        const midi = base + f;
        return {
          string: s,
          fret: f,
          midi,
          note: semitonesToNote(pitchClass, false),
          octave: Math.floor(midi / 12) - 1,
        };
      }
    }
  }

  return null;
}

/**
 * Construye la mejor ruta mnemotécnica para una nota.
 * Encuentra el mejor anclaje y genera la ruta.
 */
export function buildNoteRoute(
  noteName: string,
  maxFret: number = 15,
  instrument: InstrumentType = "guitar"
): NoteRoute | null {
  const anchor = findBestAnchor(noteName, maxFret, instrument);
  if (!anchor) return null;
  return buildRoute(anchor.string, anchor.fret, maxFret, instrument);
}

/**
 * Convierte NavCoord[] a NeckPosition[] para GuitarNeck.
 */
export function toNeckPositions(
  coords: NavCoord[],
  options: { highlightRoute?: boolean; dimOthers?: boolean } = {}
): NeckPosition[] {
  return coords.map((c, i) => ({
    string: c.string,
    fret: c.fret,
    note: c.note,
    degree: `${c.octave}` as any,
    highlight: options.highlightRoute
      ? i === 0
        ? "root"
        : "scale"
      : "scale",
  }));
}

/**
 * Lista de notas disponibles (en español para UI).
 */
export const NOTAS_NATURALES = ["Do", "Re", "Mi", "Fa", "Sol", "La", "Si"];

export const NOTAS_CON_ALTERACION: { label: string; value: string }[] = [
  { label: "Do", value: "C" },
  { label: "Do# / Reb", value: "C#" },
  { label: "Re", value: "D" },
  { label: "Re# / Mib", value: "D#" },
  { label: "Mi", value: "E" },
  { label: "Fa", value: "F" },
  { label: "Fa# / Solb", value: "F#" },
  { label: "Sol", value: "G" },
  { label: "Sol# / Lab", value: "G#" },
  { label: "La", value: "A" },
  { label: "La# / Sib", value: "A#" },
  { label: "Si", value: "B" },
];
